import {Controller} from "@hotwired/stimulus";
import {Calendar} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";

/**
 * CalendarPageController manages the calendar page layout.
 *
 * It uses FullCalendar to render the calendar.
 * See https://fullcalendar.io for more info.
 *
 * This controller handles:
 * 1. Adaptive UI: Collapses the sidebar/layout on small screens.
 * 2. Dynamic Toolbar: Switches between wide (grid) and narrow (list) views based on container width.
 *
 * Usage:
 * %div{data: { controller: 'calendar_page' }}
 *  %div
 *   %div{data: { target: 'calendar' }}
 */

export default class extends Controller {
  static targets = ['calendar'];

  /** FullCalendar instance. */
  calendarInstance = null;

  /** Minimum width in pixels before switching
   * to narrow (list) layout.
   */
  minCalendarWidth = 800;

  /** Tracked to prevent redundant view changes during resize. */
  currentCalendarView = '';

  /** Tracks layout state ('wide'|'narrow') to detect breakpoint crossings. */
  previousCalendarWidthType = '';

  /** FullCalendar headerToolbar configuration for different breakpoints. */
  calendarToolbarsTypes = {
    'wide': 'dayGridMonth,timeGridWeek,listDay,listWeek',
    'narrow': 'listMonth,listWeek,listDay'
  }

  /** Mapping between wide views
   * and their narrow counterparts
   * and vice versa.
   */
  calendarViewsMap = {
    'dayGridMonth': 'listMonth',
    'timeGridWeek': 'listWeek',
    'listDay': 'listDay',
    'listWeek': 'timeGridWeek',
    'listMonth': 'dayGridMonth',
  }

  connect() {
    super.connect();
    this._setupCalendar();
    this._getCalendarWidthType();
    this._maybeCollapseCalendarPageLayout();
  }

  disconnect() {
    super.disconnect();
    if (this.calendarInstance) {
      this.calendarInstance.destroy();
      this.calendarInstance = null;
    }
  }

  _setupCalendar() {
    this._initCalendar('dayGridMonth');
    this._setCalendarOptions();
    this._loadCalendarEvents();
    this._setCalendarHandlers();
    this._calendarRender();
  }

  _initCalendar(view) {
    this.calendarInstance = new Calendar(
      this.calendarTarget, {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
        initialView: view,
        displayEventEnd: false,
        slotMaxTime: '25:00:00',
        eventMinHeight: 48,
        nextDayThreshold: '01:00:00',
        },
    );
    this.currentCalendarView = view;
  }

  /** Construct calendar options step by step. */
  _setCalendarOptions() {
    this._setFirstDayOfWeek();
    this._setHeaderToolbarOption();
    this._setLocaleOption();
    this._setDayMaxEventRows();
    this._setTranslationOptions();
    this._setViewsOptions();
    this._setIcons();
    this._setEventColors();
  }

  _setCalendarHandlers() {
    /** Detects changes to the calendar view by the user. */
    this._setViewChangeHandler();

    /** Detects changes to the calendar width.
     * This is the main resize handler, no others are needed because
     * it responds to window resize, element resize, and zoom.
     */
    this._calendarResizeHandler();
  }

  _setFirstDayOfWeek() {
    this.calendarInstance.setOption('firstDay', 1);
  }

  _setHeaderToolbarOption() {
    this.calendarInstance.setOption('headerToolbar', {
      left: 'prev,next today',
      center: 'title',
      right: this.calendarToolbarsTypes['wide']
    });
  }

  _setLocaleOption() {
    this.calendarInstance.setOption('locale', window.I18n.locale);
  }

  /** Max events per cell/row.
   * If there are more events than this,
   * they are displayed in a popup.
   */
  _setDayMaxEventRows() {
    this.calendarInstance.setOption('dayMaxEventRows', 2);
  }

  _setTranslationOptions = () => {
    this.calendarInstance.setOption('moreLinkText', (num) => window.I18n.t('calendar.more_link_text', {count: num}));
    this.calendarInstance.setOption('buttonText', {today: window.I18n.t('calendar.today')});
    this.calendarInstance.setOption('noEventsText', window.I18n.t('calendar.no_events'));
    this.calendarInstance.setOption('eventTimeFormat', {hour: 'numeric', minute: '2-digit', meridiem: false});
    this.calendarInstance.setOption('slotLabelFormat', {hour: 'numeric', minute: '2-digit', meridiem: false});
    this.calendarInstance.setOption('allDayText', window.I18n.t('calendar.all_day'));
  }

  /** Toolbar buttons texts. */
  _setViewsOptions() {
    this.calendarInstance.setOption('buttonText', {
      today: window.I18n.t('calendar.today'),
      dayGridMonth: window.I18n.t('calendar.views.month'),
      timeGridWeek: window.I18n.t('calendar.views.week'),
      listDay: window.I18n.t('calendar.views.day_list'),
      listWeek: window.I18n.t('calendar.views.week_list'),
      listMonth: window.I18n.t('calendar.views.month_list')
    });
  }

  /** Loads calendar events from the server. */
  _loadCalendarEvents() {
    this.calendarInstance.addEventSource('/calendar/events.json');
  }

  /** Sets the current calendar view variable to
   * the one that was selected by the user
   * using the calendar toolbar buttons.
   */
  _setViewChangeHandler() {
    this.calendarInstance.setOption('datesSet', (info) => {
      this.currentCalendarView = info.view.type;

      /** Reduce unnecessary calendar height when switching to list view. */
      const isListView = info.view.type && info.view.type.startsWith('list');
      if (isListView) {
        this.calendarInstance.setOption('height', 'auto');
        this.calendarInstance.setOption('contentHeight', 'auto');
      } else {
        this.calendarInstance.setOption('height', null);
        this.calendarInstance.setOption('contentHeight', null);
      }
    });
  }

  /** Adds calendar event icons to the calendar events. */
  _setIcons() {
    this.calendarInstance.setOption('eventDidMount', (info) => {
      const icon = info.event.extendedProps?.icon;
      if (icon) {
        const titleEl = info.el.querySelector('.fc-event-title');
        if (titleEl) titleEl.insertAdjacentHTML('afterbegin', icon);
      }
    })
  }

  /** Sets the global event colors. */
  _setEventColors() {
    this.calendarInstance.setOption('eventBackgroundColor', '#f8f8f8');
    this.calendarInstance.setOption('eventBorderColor', 'rgb(0 0 0 / 0)');
    this.calendarInstance.setOption('eventTextColor', 'black');
  }

  /** Uses FullCalendar's resize handler
   * to detect changes to the calendar width.
   */
  _calendarResizeHandler() {
    this.calendarInstance.setOption('windowResize', () => {
      this._maybeCollapseCalendarPageLayout();
    })
  }

  _getCalendarWidthType() {
    this.previousCalendarWidthType = this.element.offsetWidth < this.minCalendarWidth ? 'narrow' : 'wide';
  }

  _maybeCollapseCalendarPageLayout() {
    this.element.classList.remove('collapsed-self');

    /** Force calendar to reflow. */
    this.calendarTarget.getBoundingClientRect();

    const widthAfterReflow = this.calendarTarget.offsetWidth;
    const shouldCollapse = widthAfterReflow < this.minCalendarWidth;
    this.element.classList.toggle('collapsed-self', shouldCollapse);

    this._detectCalendarWidthTypeChange();
  }

  _detectCalendarWidthTypeChange() {
    const calendarWidth = this.calendarTarget.offsetWidth;
    const currentCalendarWidthType = calendarWidth < this.minCalendarWidth ? 'narrow' : 'wide';

    if (currentCalendarWidthType !== this.previousCalendarWidthType) {
      this.previousCalendarWidthType = currentCalendarWidthType;
      this._switchCalendarToolbar(currentCalendarWidthType);
    }
  }

  _switchCalendarToolbar(currentCalendarWidthType) {
    const toolbar = this.calendarInstance.getOption('headerToolbar');
    const newToolbar = { ...toolbar, right: this.calendarToolbarsTypes[currentCalendarWidthType] };
    this.calendarInstance.setOption('headerToolbar', newToolbar);
    this._switchCalendarView();
  }

  _switchCalendarView() {
    const newView = this.calendarViewsMap[this.currentCalendarView];
    this.calendarInstance.changeView(newView);
    this.currentCalendarView = newView;
  }

  _calendarRender() {
    this.calendarInstance.render();
  }
}
